import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Loader2, CheckCircle2, FileText, Eraser, AlertTriangle, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public signature page for a Bon de mission sous-traitant.
 * Pas d'auth CRM — accès via le token unique envoyé par mail.
 *
 * SÉCURITÉ : la vue ne reçoit JAMAIS les prix du devis client, seulement
 * les désignations retenues et le montant forfaitaire convenu avec le ST.
 */
export default function SignMission() {
  const { token } = useParams();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nom, setNom] = useState('');
  const [qualite, setQualite] = useState('');
  const [acceptCgu, setAcceptCgu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const reload = useCallback(async () => {
    const res = await axios.get(`${BACKEND_URL}/api/missions/sign/${token}`);
    setMission(res.data);
    if (res.data.status === 'signed') {
      setDone({ signed_pdf_url: res.data.signed_pdf_url, signed_at: res.data.signed_at });
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try { await reload(); }
      catch (e) { setError(e?.response?.data?.detail || 'Lien invalide ou expiré.'); }
      setLoading(false);
    })();
  }, [reload]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0a1a3b';
  }, [mission]);

  const getXY = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return {
      x: ((pt.clientX - rect.left) * canvas.width) / rect.width,
      y: ((pt.clientY - rect.top) * canvas.height) / rect.height,
    };
  };
  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke();
    setHasInk(true);
  };
  const endDraw = () => { drawingRef.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const handleSign = async () => {
    setError('');
    if (!hasInk) { setError('Veuillez tracer votre signature.'); return; }
    if (!nom.trim()) { setError('Veuillez indiquer votre nom.'); return; }
    if (!acceptCgu) { setError('Veuillez accepter les conditions pour signer.'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/missions/sign/${token}/sign`,
        {
          signature_data_url: dataUrl,
          nom_signataire: nom.trim(),
          qualite_signataire: qualite.trim() || 'Signataire',
          accepte_cgu: true,
        }
      );
      setDone({ signed_pdf_url: r.data.signed_pdf_url, signed_at: r.data.signed_at });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la signature.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error && !mission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-slate-800 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  const money = (v) =>
    (typeof v === 'number'
      ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v)
      : '—');

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="max-w-xl bg-white rounded-xl shadow p-8 text-center" data-testid="mission-signed-success">
          <img
            src="/logo.png"
            alt="Reno Resolve"
            className="mx-auto mb-5 h-14 w-auto"
          />
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Bon de mission signé</h1>
          <p className="text-slate-600 mb-5">
            Signé le {new Date(done.signed_at).toLocaleString('fr-FR')}.<br />
            Un exemplaire PDF signé a été envoyé par email aux deux parties.
          </p>
          {done.signed_pdf_url && (
            <a href={done.signed_pdf_url} target="_blank" rel="noreferrer">
              <Button data-testid="mission-download-signed-pdf" className="gap-2">
                <Download className="w-4 h-4" /> Télécharger le PDF signé
              </Button>
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="text-center">
          <img
            src="/logo.png"
            alt="Reno Resolve"
            className="mx-auto mb-4 h-16 w-auto"
            data-testid="mission-sign-logo"
          />
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bon de mission sous-traitant
          </h1>
          <p className="text-slate-500 mt-1">
            Référence : <span className="font-mono">{mission.mission_id}</span>
          </p>
        </header>

        {/* Résumé parties + chantier */}
        <section className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2">Entrepreneur principal</h3>
            <p className="text-sm text-slate-700">{mission.company?.nom}</p>
            {mission.company?.representant_nom && (
              <p className="text-sm text-slate-600">
                Représenté par {mission.company.representant_nom}
              </p>
            )}
            {mission.company?.ville && (
              <p className="text-sm text-slate-600">{mission.company.ville}</p>
            )}
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-2">Sous-traitant</h3>
            <p className="text-sm text-slate-700">{mission.sous_traitant?.nom}</p>
            {mission.sous_traitant?.specialite && (
              <p className="text-sm text-slate-600">{mission.sous_traitant.specialite}</p>
            )}
            {mission.sous_traitant?.email && (
              <p className="text-sm text-slate-500">{mission.sous_traitant.email}</p>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg p-4 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-2">Chantier</h3>
          <p className="text-sm text-slate-800 font-medium">
            {mission.chantier?.project_title || mission.chantier?.chantier_id}
          </p>
          {mission.chantier?.adresse_chantier && (
            <p className="text-sm text-slate-600">
              {mission.chantier.adresse_chantier}
              {mission.chantier?.ville ? `, ${mission.chantier.ville}` : ''}
            </p>
          )}
        </section>

        {/* Prestations confiées */}
        <section className="bg-white rounded-lg p-4 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-3">Prestations confiées</h3>
          <ol className="space-y-2 text-sm text-slate-800 list-decimal list-inside">
            {(mission.designations || []).map((d, i) => (
              <li key={i} className="leading-relaxed">{d}</li>
            ))}
          </ol>
        </section>

        {/* Conditions */}
        <section className="bg-white rounded-lg p-4 border border-slate-200 space-y-2">
          <h3 className="font-semibold text-slate-900 mb-1">Conditions</h3>
          <p className="text-sm text-slate-700">
            <span className="text-slate-500">Nature :</span>{' '}
            <strong>{mission.terms?.type_prestation}</strong>
          </p>
          <p className="text-sm text-slate-700">
            <span className="text-slate-500">Montant forfaitaire convenu (HT) :</span>{' '}
            <strong className="text-blue-800">{money(mission.terms?.montant_convenu)}</strong>
          </p>
          {(mission.terms?.date_debut || mission.terms?.date_fin) && (
            <p className="text-sm text-slate-700">
              <span className="text-slate-500">Période :</span>{' '}
              {mission.terms?.date_debut || '—'} → {mission.terms?.date_fin || '—'}
            </p>
          )}
          {mission.terms?.delai_jours && (
            <p className="text-sm text-slate-700">
              <span className="text-slate-500">Délai :</span>{' '}
              {mission.terms.delai_jours} jour(s)
            </p>
          )}
          {mission.terms?.observations && (
            <p className="text-sm text-slate-700">
              <span className="text-slate-500">Observations :</span>{' '}
              {mission.terms.observations}
            </p>
          )}
        </section>

        {/* Lien PDF */}
        {mission.draft_pdf_url && (
          <section className="bg-white rounded-lg p-4 border border-slate-200">
            <a
              href={mission.draft_pdf_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 text-sm font-medium"
              data-testid="mission-view-pdf"
            >
              <FileText className="w-4 h-4" />
              Lire le bon de mission complet (PDF)
            </a>
          </section>
        )}

        {/* Signature */}
        <section className="bg-white rounded-lg p-4 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-3">Signature électronique</h3>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <Input
              placeholder="Nom et prénom du signataire"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              data-testid="mission-sign-name"
            />
            <Input
              placeholder="Qualité (ex : Gérant)"
              value={qualite}
              onChange={(e) => setQualite(e.target.value)}
              data-testid="mission-sign-role"
            />
          </div>
          <div className="border border-slate-300 rounded-lg bg-slate-50 overflow-hidden">
            <canvas
              ref={canvasRef}
              width={700}
              height={180}
              className="w-full h-[180px] touch-none bg-white block"
              onMouseDown={startDraw}
              onMouseMove={moveDraw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={moveDraw}
              onTouchEnd={endDraw}
              data-testid="mission-sign-canvas"
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <button
              type="button"
              onClick={clearCanvas}
              className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="mission-sign-clear"
            >
              <Eraser className="w-3.5 h-3.5" /> Effacer
            </button>
            <label className="text-xs text-slate-600 inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={acceptCgu}
                onChange={(e) => setAcceptCgu(e.target.checked)}
                data-testid="mission-sign-accept-cgu"
              />
              J'accepte les termes du bon de mission et confirme ma signature.
            </label>
          </div>
          {error && (
            <p className="text-xs text-red-600 mt-2">{error}</p>
          )}
          <Button
            className="w-full mt-4"
            onClick={handleSign}
            disabled={submitting}
            data-testid="mission-sign-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Signer le bon de mission
          </Button>
        </section>
      </div>
    </div>
  );
}
